import { notification } from 'antd';
import Axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { Auth } from '../constants/auth';
import { error400Config, error403Config } from './error-status-code-configs';

const createApiServices = baseURL => {
  const api = Axios.create({ baseURL });

  api.interceptors.request.use(
    (config: AxiosRequestConfig) => {
      const requestConfig = { ...config };
      const token = localStorage.getItem(Auth.ACCESS_TOKEN_KEY);

      if (token) {
        requestConfig.headers['Authorization'] = `Bearer ${token}`;
      }

      return requestConfig;
    },
    (error: AxiosError) => {
      throw error;
    }
  );

  api.interceptors.response.use(
    response => response,
    error => {
      if (error.response.status === 400) {
        const url = error?.response?.config?.url ?? '';

        const matched = error400Config.find(c => c.match(url));

        if (matched) {
          return matched.handler(error);
        }
      }

      if (error.response.status === 403) {
        const url = error?.response?.config?.url ?? '';

        const matched = error403Config.find(c => c.match(url));

        if (matched) {
          return matched.handler(error);
        }
      }

      if (error.response.status === 401) {
        if (!error.response) {
          return Promise.reject(error);
        }

        const originalRequest = error.response.config as any;

        if (originalRequest._retry) {
          document.location.replace('/login');
          return Promise.reject(error);
        }

        originalRequest._retry = true;

        const refreshToken = localStorage.getItem(Auth.REFRESH_TOKEN_KEY);

        if (!refreshToken) {
          document.location.replace('/login');
          return;
        }

        return api
          .post('/auth/refresh-tokens', {
            Token: refreshToken
          })
          .then(res => {
            if (res.status === 200) {
              const {
                data: { access_token, refresh_token }
              } = res;

              localStorage.setItem(Auth.ACCESS_TOKEN_KEY, access_token);
              localStorage.setItem(Auth.REFRESH_TOKEN_KEY, refresh_token);

              originalRequest.headers[
                'Authorization'
              ] = `Bearer ${access_token}`;

              return api(originalRequest);
            }

            document.location.replace('/login');
            return Promise.reject(error);
          })
          .catch(err => {
            localStorage.removeItem(Auth.ACCESS_TOKEN_KEY);
            localStorage.removeItem(Auth.REFRESH_TOKEN_KEY);

            return Promise.reject(err);
          });
      }

      if (error.response.status === 404) {
        notification.error({
          message: error.response?.data?.error
        });
        return Promise.reject(error);
      }

      if (error.response.status === 500 || error.response.status === 202) {
        if (error.response.config.url.includes('manually-add-titles')) {
          return error.response;
        }
      }

      notification.error({
        description: error.response?.data?.msg,
        message: error.message
      });

      throw error;
    }
  );

  return api;
};

export default createApiServices;
